/**
 * When is a failed read worth asking again?
 *
 * Pure on purpose, like `portalAccess.ts` beside it: the web test setup is
 * `node --test` with no DOM, so the decision has to be testable without
 * rendering. What is left in the component is one `queryOptions` line.
 *
 * The bug this exists to remove (QA-82): react-query's default is three retries
 * with exponential backoff, and nothing in `web/src` changes it. Measured
 * against the bundled `@tanstack/query-core` 5.101.2 by
 * `web/e2e/282-retry-schedule-probe.mjs`: a refused read makes four attempts
 * with gaps of 1000/2000/4000 ms and reaches its error state after ~7.0s. A
 * client following a stale portal link therefore watched a skeleton for 8.2s
 * before being told no -- and the answer was already in hand 7 seconds earlier.
 *
 * **A refusal is not a failure.** When the server has answered, asking again
 * cannot change the answer; it only postpones showing it. When the server has
 * not answered -- a dropped connection, a 502, a timeout -- the request really
 * might succeed next time, and the stock schedule is right. So the predicate
 * keys on the response the server gave, never on the attempt count alone.
 *
 * **Which statuses count as an answer.** Every 4xx except two. A 4xx is the
 * server stating something about this request that a retry cannot alter. The
 * exceptions are the two that explicitly invite a retry: 408 (the server gave
 * up waiting, not a verdict on the request) and 429 (a retry is the documented
 * remedy, and backoff is exactly what it asks for).
 *
 * This is deliberately the general rule rather than the specific list
 * `401/403/404`. Against Directus items that list is partly dead code:
 * `portalAccess.ts` records that Directus 11.17.4 answers 403 `FORBIDDEN` for a
 * forbidden item, a well-formed id matching no row, and a malformed id alike,
 * and never answers 404 for an item. A rule stated as "4xx except 408 and 429"
 * covers the measured cases, stays correct for the endpoints that *can* answer
 * 400 or 404, and has no branch in it that exists only to look thorough.
 *
 * **5xx and unclassified failures keep retrying.** This mirrors the decision
 * `portalAccess.ts` already makes: an unclassified error fails toward retry, so
 * a status nobody has seen yet can never be mistaken for a refusal. An error
 * with no status at all is a transport failure, which is the case retrying was
 * invented for.
 */

// Explicit .ts specifier: `tsconfig.app.json` sets `allowImportingTsExtensions`,
// and it is what lets `node --test --experimental-strip-types` resolve this file
// as well as Vite. The `@/` alias used elsewhere in this directory is a Vite
// resolution and is invisible to the test runner.
import { httpStatusOf } from "./portalAccess.ts";

/**
 * react-query's own default, restated here so the predicate is a complete
 * description of the schedule rather than a modifier on an invisible one.
 */
export const MAX_RETRIES = 3;

/**
 * Did the server answer, in a way that answering again cannot change?
 *
 * `undefined` -- no status on the error -- is not a refusal. That is a
 * transport failure, and it is the one case where retrying is most likely to
 * work.
 */
export function isAuthoritativeRefusal(status: number | undefined): boolean {
  if (typeof status !== "number") return false;
  if (status < 400 || status >= 500) return false;
  // The two 4xx that ask to be retried rather than forbidding it.
  if (status === 408 || status === 429) return false;
  return true;
}

/**
 * Drop-in for a react-query `retry` option.
 *
 * `failureCount` is the number of failures *already* recorded, so the stock
 * three-retry schedule is `failureCount < 3` -- attempts at 0, 1 and 2 retry,
 * the attempt at 3 gives up. Verified against the bundled library rather than
 * assumed: `retryer.js:86` reads `config.retry ?? (isServer() ? 0 : 3)` and
 * compares `failureCount < retry`.
 */
export function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  if (isAuthoritativeRefusal(httpStatusOf(error))) return false;
  return failureCount < MAX_RETRIES;
}
